<template>
  <div class="max-w-[100%]">
    <!-- Mobile Layout -->
    <Card v-if="isMobile" class="mb-6 rounded-2xl shadow-md relative" :class="cardClass">
      <div v-if="showOverlay" class="absolute inset-0 bg-grey-light-50/50 rounded-2xl z-10" />
      <CardContent class="p-6">
        <div class="mb-4">
          <h1 class="text-[18px] font-semibold mb-1" :class="titleClass">
            {{ merchantName }}
          </h1>
          <p class="text-sm" :class="subtitleClass">
            Reference number <span class="font-medium" :class="referenceClass">{{ reference }}</span>
          </p>
        </div>

        <div class="mb-4">
          <img :src="imageUrl" alt="Product" class="w-full h-[200px] object-contain rounded-xl" />
        </div>

        <div v-if="!hideProgress" class="relative mb-6">
          <div class="flex gap-0 mb-0">
            <div class="relative h-2 w-full overflow-hidden bg-grey-light-200" :class="progressBarClass">
              <div class="h-full bg-brand-600 transition-all" :style="{ width: `${progress}%` }" />
            </div>
            <slot name="progress-icon" />
          </div>
          <div class="flex justify-between items-center" :class="progressMarginClass">
            <div class="items-center gap-2">
              <slot name="threshold-badge" />
              <span class="text-[14px]" :class="progressTextClass">{{ remainingTime }}</span>
            </div>
            <span class="text-sm font-medium" :class="progressTextClass">{{ progress }}%</span>
          </div>
        </div>

        <slot name="mobile-fields" />
      </CardContent>
      <hr />
      <div class="flex gap-2 p-6">
        <slot name="mobile-actions" />
      </div>
    </Card>

    <!-- Desktop Layout -->
    <div
      v-else
      class="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6 mb-6 rounded-2xl p-6 shadow-md relative"
      :class="cardClass"
    >
      <div v-if="showOverlay" class="absolute inset-0 bg-grey-light-50/50 rounded-2xl z-10" />
      <div class="flex items-center justify-center">
        <img :src="imageUrl" alt="Product" class="w-full h-[300px] object-contain rounded-2xl" />
      </div>

      <div class="space-y-6">
        <div class="flex justify-between items-start">
          <div>
            <div class="flex items-center">
              <h1 class="text-[20px] font-semibold mb-1" :class="titleClass">
                {{ merchantName }}
              </h1>
              <slot name="desktop-badge" />
            </div>
            <p class="text-[14px]" :class="subtitleClass">
              Reference number <span class="font-medium" :class="referenceClass">{{ reference }}</span>
            </p>
          </div>
          <div class="flex gap-2">
            <slot name="desktop-actions" />
          </div>
        </div>

        <div v-if="!hideProgress" class="relative">
          <div class="flex gap-0 mb-0">
            <div class="relative h-2 w-full overflow-hidden bg-grey-light-200" :class="progressBarClass">
              <div class="h-full bg-brand-600 transition-all" :style="{ width: `${progress}%` }" />
            </div>
            <slot name="progress-icon" />
          </div>
          <div class="flex justify-between items-center" :class="progressMarginClass">
            <div class="flex items-center gap-2">
              <slot name="threshold-badge" />
              <span class="text-[14px]" :class="progressTextClass">{{ remainingTime }}</span>
            </div>
            <span class="text-sm font-medium" :class="progressTextClass">{{ progress }}%</span>
          </div>
        </div>

        <slot name="desktop-fields" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Card, CardContent } from '@/components/ui/card';
import { computed } from 'vue';

interface Props {
  merchantName: string;
  reference: string;
  imageUrl: string;
  progress: number;
  remainingTime: string;
  showOverlay?: boolean;
  isMobile?: boolean;
  isRedeemed?: boolean;
  isCancelled?: boolean;
  hideProgress?: boolean;
  progressBarClass?: string;
  progressMarginClass?: string;
  progressTextClass?: string;
  valueTextClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  showOverlay: false,
  isMobile: false,
  isRedeemed: false,
  isCancelled: false,
  hideProgress: false,
  progressBarClass: 'rounded-full',
  progressMarginClass: 'mt-2',
  progressTextClass: 'text-brand-600',
  valueTextClass: 'text-brand-700',
});

const cardClass = computed(() => {
  if (props.isRedeemed) {
    return 'border-[1px] border-boost-500 bg-boost-25';
  }
  return 'border';
});

const titleClass = computed(() => {
  if (props.isRedeemed) return 'text-boost-500';
  return 'text-grey-light-900';
});

const subtitleClass = computed(() => {
  if (props.isRedeemed) return 'text-boost-500';
  return 'text-grey-light-600';
});

const referenceClass = computed(() => {
  if (props.isRedeemed) return 'text-boost-500';
  return 'text-grey-light-900';
});
</script>
